import { useMemo, useState } from "react";
import { Check, Phone, ChevronRight, Clock, CalendarPlus, AlertTriangle, Inbox } from "lucide-react";
import GuideBanner from "../components/GuideBanner";
import Badge from "../components/Badge";
import { fmtShort, getToday } from "../utils";
import { agendar, promessasSemTarefa } from "../lib/followup";
import { somarDias } from "../lib/abordagem";

// ═══════════════════════════════════════════════════════════════
// TAREFAS E FOLLOW-UP
// ═══════════════════════════════════════════════════════════════
//
// A lista antiga mostrava o título e mais nada: para agir era preciso decorar
// o nome da empresa, sair da aba, achar o card e só então registrar. Follow-up
// que dá trabalho para começar não é feito.
//
// Aqui cada linha abre a empresa, registra o contato ou reagenda com um clique
// — e quem está atrasado diz há quantos dias, porque "atrasada" sem número não
// diferencia o de ontem do de três semanas atrás.

export default function Tasks({
  tasks = [], today = getToday(), toggleTask, overdueTasks = [], todayTasks = [], upcomingTasks = [],
  partners = [], clients = [], setTasks, userId,
  onAbrirParceiro, onAbrirCliente, onRegistrarContato,
}) {
  const [filtro, setFiltro] = useState("all");

  const acharParceiro = (id) => partners.find((p) => p.id === id);
  const acharCliente = (id) => clients.find((c) => c.id === id);

  // Promessas antigas que nunca viraram tarefa (feitas antes de a retomada
  // gerar lembrete). Sem isto, elas continuariam invisíveis para sempre.
  const orfas = useMemo(() => promessasSemTarefa(partners, tasks), [partners, tasks]);

  const filtrar = (lista) => lista.filter((t) => filtro === "all" || t.type === filtro);

  const grupos = [
    { id: "overdue", label: "Atrasadas", items: filtrar(overdueTasks), color: "#ef4444" },
    { id: "today", label: "Para hoje", items: filtrar(todayTasks), color: "#f59e0b" },
    { id: "upcoming", label: "Próximas", items: filtrar(upcomingTasks).slice(0, 15), color: "#3b82f6" },
    { id: "done", label: "Concluídas recentemente", items: filtrar(tasks.filter((t) => t.done)).slice(-5).reverse(), color: "#059669" },
  ];

  const reagendar = (t, dias) => {
    if (!setTasks) return;
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, dueDate: somarDias(today, dias) } : x)));
  };

  const adotarOrfa = (o) => {
    if (!setTasks) return;
    setTasks((prev) => agendar(prev, {
      refId: o.parceiro.id, titulo: `${o.titulo} — ${o.parceiro.name}`,
      quando: o.quando, userId, notas: o.notas,
    }));
  };

  const total = overdueTasks.length + todayTasks.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <GuideBanner
        title="Tarefas e Follow-ups"
        text="Todo compromisso com data aparece aqui — inclusive a retomada de quem disse 'agora não'. Clique na linha para abrir a empresa, registre o contato direto ou reagende. Tarefa reagendada substitui a anterior: nunca ficam três lembretes da mesma empresa."
      />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { id: "all", label: "Tudo" },
            { id: "partner", label: "Parceiros" },
            { id: "client", label: "Clientes" },
          ].map((f) => (
            <button
              key={f.id} onClick={() => setFiltro(f.id)}
              style={{
                padding: "6px 13px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${filtro === f.id ? "#7c3aed" : "#e2e8f0"}`,
                background: filtro === f.id ? "#f5f3ff" : "#fff",
                color: filtro === f.id ? "#6d28d9" : "#64748b",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        {total > 0 && (
          <div style={{ fontSize: 11.5, color: overdueTasks.length ? "#b91c1c" : "#64748b", fontWeight: 600 }}>
            {overdueTasks.length > 0 && <>{overdueTasks.length} atrasada{overdueTasks.length > 1 ? "s" : ""} · </>}
            {todayTasks.length} para hoje
          </div>
        )}
      </div>

      {/* Promessas que ficaram para trás */}
      {orfas.length > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
            <AlertTriangle size={14} /> {orfas.length} compromisso{orfas.length > 1 ? "s" : ""} sem lembrete
          </div>
          <div style={{ fontSize: 11, color: "#92400e", lineHeight: 1.45, marginBottom: 8 }}>
            Estas empresas têm data de retomada marcada no card, mas foram registradas antes de a retomada virar tarefa.
            Traga para a lista para não perder de novo.
          </div>
          {orfas.slice(0, 8).map((o) => (
            <div key={o.parceiro.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "5px 0", fontSize: 11.5, borderTop: "1px solid #fef3c7" }}>
              <span style={{ color: "#78350f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {o.parceiro.name} <span style={{ color: "#b45309" }}>· {o.titulo.toLowerCase()} em {fmtShort(o.quando)}</span>
              </span>
              <button onClick={() => adotarOrfa(o)} style={{ padding: "3px 10px", borderRadius: 6, border: "none", background: "#d97706", color: "#fff", fontSize: 10.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                Criar tarefa
              </button>
            </div>
          ))}
        </div>
      )}

      {grupos.map((g) => (
        <div key={g.id} style={{ background: "#fff", borderRadius: 10, padding: "14px 16px", border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: g.color, marginBottom: 10 }}>
            {g.label} ({g.items.length})
          </div>
          {g.items.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#94a3b8", padding: "6px 0" }}>
              <Inbox size={13} /> {g.id === "overdue" ? "Nada atrasado — está em dia." : "Nenhuma tarefa aqui."}
            </div>
          ) : (
            g.items.map((t) => {
              const parceiro = t.type === "partner" ? acharParceiro(t.refId) : null;
              const cliente = t.type === "client" ? acharCliente(t.refId) : null;
              const alvo = parceiro || cliente;
              const atraso = !t.done && t.dueDate < today
                ? Math.round((new Date(`${today}T00:00:00Z`) - new Date(`${t.dueDate}T00:00:00Z`)) / 86400000)
                : 0;
              return (
                <div key={t.id} style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "9px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <button
                    onClick={() => toggleTask(t.id)}
                    title={t.done ? "Reabrir" : "Concluir"}
                    style={{
                      width: 18, height: 18, borderRadius: 4, border: `2px solid ${t.done ? "#059669" : "#d1d5db"}`,
                      background: t.done ? "#059669" : "transparent", cursor: "pointer", flexShrink: 0, marginTop: 2,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {t.done && <Check size={11} color="#fff" />}
                  </button>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      onClick={() => {
                        if (parceiro && onAbrirParceiro) onAbrirParceiro(parceiro);
                        else if (cliente && onAbrirCliente) onAbrirCliente(cliente);
                      }}
                      style={{
                        fontSize: 12.5, fontWeight: 600, cursor: alvo ? "pointer" : "default",
                        color: t.done ? "#94a3b8" : "#1e293b", textDecoration: t.done ? "line-through" : "none",
                        display: "flex", alignItems: "center", gap: 4,
                      }}
                    >
                      {t.title}{alvo && <ChevronRight size={12} color="#94a3b8" />}
                    </div>
                    {t.notes && <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 2 }}>{t.notes}</div>}

                    {!t.done && alvo && (
                      <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
                        {parceiro && onRegistrarContato && (
                          <button onClick={() => onRegistrarContato(parceiro, t.id)} style={acao("#7c3aed", "#f5f3ff", "#ddd6fe")}>
                            <Phone size={11} /> Registrar contato
                          </button>
                        )}
                        {setTasks && (
                          <>
                            <button onClick={() => reagendar(t, 1)} style={acao("#475569", "#fff", "#e2e8f0")} title="Empurrar para amanhã">
                              <CalendarPlus size={11} /> Amanhã
                            </button>
                            <button onClick={() => reagendar(t, 7)} style={acao("#475569", "#fff", "#e2e8f0")} title="Empurrar uma semana">
                              +7 dias
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <Badge color={t.type === "client" ? "#2563eb" : t.type === "partner" ? "#7c3aed" : "#64748b"} small>
                      {t.type === "client" ? "Cliente" : t.type === "partner" ? "Parceiro" : "Interno"}
                    </Badge>
                    <div style={{ fontSize: 9.5, color: atraso ? "#dc2626" : "#94a3b8", marginTop: 3, fontWeight: atraso ? 700 : 400, display: "flex", alignItems: "center", gap: 3, justifyContent: "flex-end" }}>
                      {atraso > 0 && <Clock size={9} />}
                      {atraso > 0 ? `${atraso} ${atraso === 1 ? "dia" : "dias"} atrás` : fmtShort(t.dueDate)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ))}
    </div>
  );
}

const acao = (cor, fundo, borda) => ({
  display: "flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 6,
  border: `1px solid ${borda}`, background: fundo, color: cor, fontSize: 10.5, fontWeight: 600, cursor: "pointer",
});
